import { BookOpen } from "lucide-react";

// Base 3 examples from the source article:
// https://www.autotuner.com/de/blogs/neuigkeiten/bosch-ecu-references
// The rest are additional real Bosch ECU designations (several verified live
// against flashtec.ch's Bosch hardware-number lookup), decoded using the
// same letter-scheme the article establishes. Trailing type numbers are
// only attributed to a specific OEM where the source article states it
// explicitly — for the added codes that reservation isn't public, so they're
// labeled generically instead of guessing a manufacturer.
const GROUPS = [
  {
    title: "Benzin · Motronic-Familie (M…)",
    note: "Der 2. Buchstabe zeigt, ob die Drosselklappe elektronisch (E) oder noch mechanisch per Seilzug gesteuert wird — fehlt das E, ist es eine ältere, kabelgesteuerte Motronic. Ein D markiert Direkteinspritzung; fehlt es, spritzt der Motor ins Saugrohr statt direkt in den Brennraum.",
    examples: [
      {
        code: "MED17.1.1",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "E", meaning: "Elektronische Drosselklappensteuerung (nicht kabelgesteuert)." },
          { letters: "D", meaning: "Direkteinspritzsystem." },
          { letters: "17", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "1.1", meaning: "Steuergerätetyp (hier für VAG reserviert)." },
        ],
      },
      {
        code: "MG1CS201",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "G", meaning: "Gasoline — ein Benzinmotor." },
          { letters: "1", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "C", meaning: "Common-Rail-Einspritzsystem." },
          { letters: "S", meaning: "Solenoid — Magnetventil-Einspritzdüsen." },
          { letters: "201", meaning: "ECU-Typ (hier für BMW reserviert)." },
        ],
      },
      {
        code: "ME7.9.10",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "E", meaning: "Elektronische Drosselklappensteuerung." },
          { letters: "—", meaning: "Kein D: Saugrohreinspritzung statt Direkteinspritzung." },
          { letters: "7", meaning: "Bosch-Version des Steuergeräts (ältere Generation als ME9/MED17)." },
          { letters: "9.10", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
      {
        code: "ME9.6.1",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "E", meaning: "Elektronische Drosselklappensteuerung." },
          { letters: "9", meaning: "Bosch-Version des Steuergeräts (Nachfolgegeneration von ME7)." },
          { letters: "6.1", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
      {
        code: "M7.9.52",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "—", meaning: "Kein E: klassische, kabelgesteuerte Drosselklappe (kein Drive-by-Wire)." },
          { letters: "7", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "9.52", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
    ],
  },
  {
    title: "Diesel · EDC- & MD-Familie",
    note: "Diesel-Steuergeräte laufen bei Bosch unter einem eigenen Präfix statt „M…“: EDC (Electronic Diesel Control) für die klassische Linie, MD1 für die neuere, zu MG1 parallele Plattform. C/CP steht für Common-Rail (mit Piezo-Injektoren bei „CP“), U für ältere Pumpe-Düse-Systeme (Unit Injector).",
    examples: [
      {
        code: "MD1CP002",
        parts: [
          { letters: "M", meaning: "Motronic — die Bezeichnung für das elektronische Steuergerät von Bosch." },
          { letters: "D", meaning: "Diesel — ein Dieselmotor." },
          { letters: "1", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "C", meaning: "Common-Rail-Einspritzsystem." },
          { letters: "P", meaning: "Piezo — piezoelektrische Einspritzdüsen." },
          { letters: "002", meaning: "ECU-Typ (hier für Mercedes reserviert)." },
        ],
      },
      {
        code: "EDC17C46",
        parts: [
          { letters: "EDC", meaning: "Electronic Diesel Control — Bosch' klassische Diesel-Steuergeräte-Familie." },
          { letters: "17", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "C", meaning: "Common-Rail-Einspritzsystem." },
          { letters: "46", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
      {
        code: "EDC17CP02",
        parts: [
          { letters: "EDC", meaning: "Electronic Diesel Control — Bosch' klassische Diesel-Steuergeräte-Familie." },
          { letters: "17", meaning: "Bosch-Version des Steuergeräts." },
          { letters: "C", meaning: "Common-Rail-Einspritzsystem." },
          { letters: "P", meaning: "Piezo — piezoelektrische Einspritzdüsen." },
          { letters: "02", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
      {
        code: "EDC16U31",
        parts: [
          { letters: "EDC", meaning: "Electronic Diesel Control — Bosch' klassische Diesel-Steuergeräte-Familie." },
          { letters: "16", meaning: "Bosch-Version des Steuergeräts (Vorgänger von EDC17)." },
          { letters: "U", meaning: "Unit Injector — Pumpe-Düse-Einspritzsystem statt Common-Rail." },
          { letters: "31", meaning: "Steuergerätetyp (herstellerspezifisch)." },
        ],
      },
    ],
  },
];

function ExampleCard({ code, parts }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-6 space-y-5">
      <div
        className="text-2xl sm:text-3xl tracking-tight text-[#D32222]"
        style={{ fontFamily: "'Orbitron', sans-serif" }}
      >
        {code}
      </div>
      <div className="divide-y divide-neutral-800/70">
        {parts.map((p, i) => (
          <div key={`${p.letters}-${i}`} className="flex items-start gap-4 py-3 first:pt-0 last:pb-0">
            <span
              className="shrink-0 min-w-[3rem] text-center rounded border border-[#D32222]/40 bg-[#D32222]/10 text-[#D32222] text-sm font-mono px-2 py-1"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {p.letters}
            </span>
            <span className="text-sm text-neutral-300 leading-relaxed">{p.meaning}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExampleGroup({ title, note, examples }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl sm:text-2xl tracking-tight" style={{ fontFamily: "'Orbitron', sans-serif" }}>
          {title}
        </h2>
        <p className="mt-2 text-sm text-neutral-400 max-w-3xl leading-relaxed">{note}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {examples.map((ex) => (
          <ExampleCard key={ex.code} {...ex} />
        ))}
      </div>
    </div>
  );
}

export default function BoschEcuReferences() {
  return (
    <div data-testid="bosch-ecu-references-page" className="space-y-10">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D32222] mb-3 flex items-center gap-2">
          <BookOpen className="w-3 h-3" /> Wissen · Referenz
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Bosch-ECU-Referenznummern
          <br />
          <span className="text-[#D32222]">entschlüsselt</span>.
        </h1>
        <p className="mt-4 text-neutral-400 max-w-2xl text-sm sm:text-base">
          Bosch benennt seine Steuergeräte nach einem festen Schema statt
          zufälliger Modellnummern. Wer den Code lesen kann, erkennt sofort
          Motorart, Einspritzsystem und Bosch-Generation — unabhängig vom
          Hersteller.
        </p>
      </div>

      {GROUPS.map((group) => (
        <ExampleGroup key={group.title} {...group} />
      ))}

      <div className="rounded-xl border border-neutral-800 bg-panel p-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
          Grober Aufbau
        </div>
        <p className="text-sm text-neutral-300 leading-relaxed">
          1. Buchstabe(n) = Familie (M = Motronic/Benzin, EDC = Electronic
          Diesel Control) · 2. Buchstabe = Kraftstoffart/Funktion (E =
          elektronische Drosselklappe, D = Diesel, G = Benzin/Gasoline) ·
          Ziffer danach = Bosch-Generation · weitere Buchstaben =
          Einspritzsystem/Ventiltyp (C = Common-Rail, U = Pumpe-Düse, S =
          Solenoid, P = Piezo) · letzte Zahlengruppe = herstellerspezifischer
          ECU-Typ.
        </p>
      </div>
    </div>
  );
}
